/**
 * Hook managing a standalone VTK VOLUME_3D viewport for a report page.
 *
 * Delegates the full create→wire→toolgroup→resize→observe sequence to
 * ViewportService.initStandaloneViewport(), keeping React as a thin wrapper.
 */

import { useEffect, useRef, useState } from 'react';

import { useVisualization } from '@/context';
import type { Color3DPresetId } from '@/visualization/services/color-3d-presets';
import { ViewportType } from '@/visualization/viewports/types';

export interface UseReportViewportOptions {
    viewportId: string;
    backgroundColor?: [number, number, number];
    /** 3D rendering profile pinned on the viewport. Defaults to the report profile. */
    color3DPresetId?: Color3DPresetId;
}

const DEFAULT_BACKGROUND_COLOR: [number, number, number] = [0.96, 0.96, 0.96];

/** Flat-lit profile tuned for the report's light background. */
const DEFAULT_COLOR_3D_PRESET_ID: Color3DPresetId = 'report';

export function useReportViewport(options: UseReportViewportOptions) {
    const {
        viewportId,
        backgroundColor = DEFAULT_BACKGROUND_COLOR,
        color3DPresetId = DEFAULT_COLOR_3D_PRESET_ID,
    } = options;
    const { getViewportService } = useVisualization();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [error, setError] = useState<Error | null>(null);

    // Depend on the color components, not the array reference: a caller passing an
    // inline literal (or the default) allocates a fresh array each render, which
    // would otherwise re-run this effect every render and recreate the viewport.
    const [bgR, bgG, bgB] = backgroundColor;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        try {
            const handle = getViewportService().initStandaloneViewport(viewportId, {
                viewportType: ViewportType.VOLUME_3D,
                container,
                showOrientationWidget: false,
                backgroundColor: [bgR, bgG, bgB],
                ignoreEvent: true,
                color3DPresetId,
            });
            return () => handle.dispose();
        } catch (err) {
            console.error('[useReportViewport] Failed to initialize viewport:', err);
            setError(err instanceof Error ? err : new Error('Viewport initialization failed'));
            return;
        }
    }, [getViewportService, viewportId, bgR, bgG, bgB, color3DPresetId]);

    return { containerRef, error };
}
